import i18nLib, { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { ENV } from 'shared/ENV';
import { isProductionEnvironment } from 'shared/envUtils';
import EN from './locales/en/common.json';
import ES from './locales/es/common.json';
import TL from './locales/tl/common.json';
import VI from './locales/vi/common.json';
import ZH from './locales/zh/common.json';
import {
  DEFAULT_LOCALE,
  getNormalizedLanguageCode,
  Language,
  mapDeviceLangToSupported,
  setLanguage,
  SUPPORTED_LANGUAGES,
} from './utils';

export const options: InitOptions = {
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LANGUAGES,
  // make sure we're getting `en`, not `en-us` from language detector
  load: 'languageOnly',
  debug: !isProductionEnvironment() && ENV.VITE_ENV !== 'test',
  ns: ['common'],
  defaultNS: 'common',
  resources: {
    en: {
      common: EN,
    },
    es: {
      common: ES,
    },
    tl: {
      common: TL,
    },
    vi: {
      common: VI,
    },
    zh: {
      common: ZH,
    },
  },
  // options for LanguageDetector
  detection: {
    order: ['localStorage', 'navigator'],
  },
  interpolation: {
    escapeValue: false, // not needed for react as it escapes by default (per https://react.i18next.com/latest/using-with-hooks)
  },
};

export const i18n = i18nLib
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next);

const storedLang = localStorage.getItem('i18nextLng');

// Only use device language if user hasn't selected one before
const lng = storedLang
  ? undefined // let detector use stored value
  : mapDeviceLangToSupported(getNormalizedLanguageCode());

// init i18next
// for all options read: https://www.i18next.com/overview/configuration-options
i18n.init({ ...options, lng });

// for initial load - if the user is auto-detected into a language
// (`resolved` reflects what i18next actually loaded, handling cases where
// `lng` is undefined or initialization falls back to default)
const resolved = i18n.resolvedLanguage;
if (resolved && resolved !== DEFAULT_LOCALE) {
  // ensure that date-fns uses the correct locale as well
  setLanguage(resolved as Language);
}
